/**
 * Competition · sport · kickoff, as both fixture rows write it.
 *
 * The handoff draws two meta lines — `.vw-row-meta` on the Contest Overview's
 * read-only Fixtures section (CAE-1573) and `.m-row-meta` on Edit › Fixtures'
 * `.m-row` (CAE-1550) — and they are the same three clauses from the same three
 * payload fields. What differs between them is type, not content: `.m-row-when`
 * tightens the kickoff by `-0.01em` and `.vw-row-meta` does not. So the
 * composition is shared and the tracking is a prop, rather than the rule that
 * decides *which clauses appear* living in two files.
 *
 * That rule is the part worth sharing. Competition and sport are the
 * catalogue's answer and go missing when it drops a fixture, so each is a
 * clause that either appears with its separator or does not appear at all. The
 * kickoff is the contest's own and is always there, which is why it anchors the
 * line and carries no separator of its own.
 *
 * The container's own font size and colour stay with the caller — the two rows
 * agree on 12.5/`--ink-3` today, and that is their rule each, not this one's.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import type { ContestDetailFixture } from '../../api/contests';
import { ViewMetaDot } from '../../components/ui';
import { formatSportLabel } from '../../utils/sport-label';
import { formatKickoff } from './overview/fixture-display';

export interface FixtureMetaClausesProps {
  fixture: Pick<ContestDetailFixture, 'competitionName' | 'sportCode' | 'startTime'>;
  /** `.m-row-when`'s `-0.01em`; the Overview's line sets none. */
  kickoffTracking?: string;
}

export const FixtureMetaClauses = ({ fixture, kickoffTracking }: FixtureMetaClausesProps) => {
  const sport = formatSportLabel(fixture.sportCode);

  return (
    <>
      {fixture.competitionName ? (
        <>
          <Box
            component="span"
            sx={(theme: Theme) => ({ color: theme.tokens.color.ink2, fontWeight: 500 })}
          >
            {fixture.competitionName}
          </Box>
          <ViewMetaDot />
        </>
      ) : null}
      {sport ? (
        <>
          <span>{sport}</span>
          <ViewMetaDot />
        </>
      ) : null}
      {/* Mono and tabular, per §1: a column of kickoffs lines up on the digits. */}
      <Box
        component="span"
        sx={(theme: Theme) => ({
          fontFamily: theme.tokens.font.mono,
          fontVariantNumeric: 'tabular-nums',
          ...(kickoffTracking ? { letterSpacing: kickoffTracking } : {}),
        })}
      >
        {formatKickoff(fixture.startTime)}
      </Box>
    </>
  );
};
